import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import PlayerMatchingModal from '@/components/shared/PlayerMatchingModal';
import { getApiUrl } from '@/config/api';
import { CheckCircle2 } from '@/utils/icons';

const ConnectTerraCoach: React.FC = () => {
    const navigate = useNavigate();
    const { isDark } = useTheme();
    const [token, setToken] = useState('');
    const [region, setRegion] = useState('us');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showMatching, setShowMatching] = useState(false);
    const [userInfo, setUserInfo] = useState<{ username: string; role?: string; full_name?: string; first_name?: string } | null>(null);

    useEffect(() => {
        const access = localStorage.getItem('access');
        if (!access) {
            navigate('/login');
            return;
        }
        const userInfoString = localStorage.getItem('user_info');
        if (userInfoString) {
            try {
                setUserInfo(JSON.parse(userInfoString));
            } catch {
                // ignore
            }
        }
    }, [navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token.trim()) {
            setError('Please enter your Catapult API token.');
            return;
        }
        const access = localStorage.getItem('access');
        if (!access) {
            navigate('/login');
            return;
        }
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const response = await fetch(getApiUrl('/api/terra/teams/create-coach/'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${access}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token: token.trim(), region }),
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                setToken('');
                setShowMatching(true);
            } else {
                setError(data.error || data.details || `Request failed (${response.status})`);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to connect. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="cgm-bg-primary min-h-screen flex flex-col items-center justify-center py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300">

            <div className="max-w-md w-full space-y-6">
                <div className="text-center">
                    <h2 className="cgm-text-primary text-3xl font-light tracking-tight mb-2">
                        Connect Catapult Vector
                    </h2>
                    <p className="cgm-text-secondary text-sm">
                        Enter your Catapult OpenField API token to link your team account.
                    </p>
                </div>

                {error && (
                    <div className="cgm-alert-error px-4 py-3 rounded-md text-sm">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="cgm-alert-success px-4 py-3 rounded-md text-sm flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 shrink-0" />
                        {success}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="cgm-card p-6 space-y-4">
                    <div>
                        <label htmlFor="catapult-token" className="block cgm-text-primary text-sm font-medium mb-1">
                            Catapult API Token
                        </label>
                        <input
                            id="catapult-token"
                            type="password"
                            value={token}
                            onChange={(e) => setToken(e.target.value)}
                            placeholder="Paste your Catapult API token"
                            className="cgm-input w-full"
                            disabled={loading}
                            autoComplete="off"
                        />
                        <p className="cgm-text-secondary text-xs mt-1">
                            Get a token from Catapult OpenField Cloud: Settings → API Tokens
                        </p>
                    </div>
                    <div>
                        <label htmlFor="catapult-region" className="block cgm-text-primary text-sm font-medium mb-1">
                            Region
                        </label>
                        <select
                            id="catapult-region"
                            value={region}
                            onChange={(e) => setRegion(e.target.value)}
                            className="cgm-input w-full"
                            disabled={loading}
                        >
                            <option value="us">US (connect-us)</option>
                            <option value="au">Australia (connect-au)</option>
                            <option value="eu">Europe (connect-eu)</option>
                        </select>
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className={`cgm-button w-full px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                            loading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                    >
                        {loading ? 'Connecting...' : 'Connect Catapult'}
                    </button>
                </form>
            </div>

            {showMatching && (
                <PlayerMatchingModal
                    provider="catapult"
                    onNothingToReview={() => {
                        setShowMatching(false);
                        setSuccess('Catapult connected! All players already linked. Data will sync shortly.');
                    }}
                    onClose={() => {
                        setShowMatching(false);
                        setSuccess('Catapult connected! Your team data will sync shortly.');
                    }}
                    onComplete={(results) => {
                        setShowMatching(false);
                        const parts = [];
                        if (results.linked > 0) parts.push(`${results.linked} linked`);
                        if (results.created > 0) parts.push(`${results.created} created`);
                        if (results.skipped > 0) parts.push(`${results.skipped} skipped`);
                        setSuccess(
                            `Catapult connected! Players: ${parts.join(', ') || 'none processed'}. Data will sync shortly.`
                        );
                    }}
                />
            )}
        </div>
    );
};

export default ConnectTerraCoach;
